import type {DynamoEnvValueType} from '../types';
import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TText,
  TListOfElement,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {
  dynamoTextualContentToListOfElements,
  dynamoTextualContentToText,
} from '../transformers';
import {TOption} from '@packages/systems/plugins/Option';
import {TDynamoLink} from '@packages/systems/plugins-utils/DynamoGateway';
import {
  LinkPreloadValue,
  LinkTargetValue,
  TLinkPreload,
  TLinkTarget,
} from '../toBasicLinkV2Shared';

export const LinkType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('Link'))),
});

export const TBasicLink = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('Link'))
);

export const dynamoLinkToText: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoLink)),
      TText
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: dynamoTextualContentToText,
};

export const dynamoLinkToListOfElements: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoLink)),
      TListOfElement
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: dynamoTextualContentToListOfElements,
};

export type ExternalLinkLiteral = {
  url: string;
  mode: 'external';
  preload?: 'prefetch' | 'prerender';
  target?: '_blank';
};

/**
 * @deprecated - replaced by the V2 implicit below
 *
 * Should be exported as hidden to ensure backwards compatibility before
 * we have run the site data migration to remove calls to this function
 * from element data.
 *
 * Should be deleted after the migration has been shipped.
 */
export const dynamoLinkToBasicLink: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoLink)),
      TBasicLink
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<string>): ExternalLinkLiteral => {
    if (
      option === null ||
      option.tag === 'None' ||
      typeof option.val !== 'string' ||
      !option.val
    ) {
      return {
        url: '',
        mode: 'external',
      };
    }

    return {
      url: addCleanHrefShortCircuitFlag(option.val),
      mode: 'external',
    };
  },
};

export const dynamoLinkToBasicLinkV2: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Link settings'},
      functionOf(
        TLinkTarget,
        functionOf(
          TLinkPreload,
          functionOf(
            withSystem({isBindable: true}, TOption(TDynamoLink)),
            TBasicLink
          )
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (target: LinkTargetValue) => {
    return (preload: LinkPreloadValue) => {
      return (option: DynamoEnvValueType<string>): ExternalLinkLiteral => {
        const openInNewTabBool = target.tag === 'New tab';
        const preloadValue = preload.tag.toLowerCase() as
          | 'none'
          | 'prefetch'
          | 'prerender';
        if (
          option === null ||
          option.tag === 'None' ||
          typeof option.val !== 'string' ||
          !option.val
        ) {
          return {
            url: '',
            mode: 'external',
          };
        }

        return {
          url: addCleanHrefShortCircuitFlag(option.val),
          mode: 'external',
          ...(preloadValue !== 'none' ? {preload: preloadValue} : null),
          ...(openInNewTabBool ? {target: '_blank'} : null),
        };
      };
    };
  },
};

/**
 * Adds backtick character as prefix to the url string. This short circuits
 * HtmlUtils.cleanHref used by Link renderer, which is not called on legacy
 * Dynamo link href binding values.
 */
function addCleanHrefShortCircuitFlag(url: string) {
  return `\`${url}`;
}
